import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Mail, MessageSquare, Download, Plus, X, ChevronDown, ChevronLeft, ChevronRight,
  Paperclip, Lock, Filter, CalendarDays, Sparkles, Loader2, FileText, FileType, AlertTriangle, Pencil,
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { fmtTime12, isoDate } from '../../utils/time';
import { codeWords } from '../../utils/format';
import { useClientRecord, ClientFrame } from './ClientLayout';
import { fullName, primaryContactOf } from './clientShared';
import VoiceButton from '../../components/VoiceButton';
import { useNoteTypes } from '../../hooks/useSettings';
import { format, parseISO, addMonths, subMonths, startOfMonth, getDaysInMonth, getDay, isToday, isSameDay, isWithinInterval, startOfWeek, endOfWeek, startOfMonth as sfm, endOfMonth } from 'date-fns';
import clsx from 'clsx';
import { exportProgressPDF, exportProgressDocx } from '../../utils/exportProgressNotes';
import { getBranding } from '../../utils/exportBranding';

// ─── Filter categories ────────────────────────────────────────────────────────
// Categories are derived at runtime from the note types that ACTUALLY exist on
// the client's notes (see catOptions), so every filter checkbox is meaningful.
// Canonicalise so 'progress' (mobile) and 'Progress' (web) collapse to one.
// 'shift_note' and 'Shift note' are one category: underscores read as spaces, so the filter chip
// says 'Shift note' rather than 'Shift_note'.
const canonCat = (t) => {
  const s = String(t || 'Note').replace(/_/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : 'Note';
};

// Incident shown inline in the Communications timeline (no separate tab).
const INCIDENT_CAT_LABEL = {
  category_1: 'Category 1', category_2: 'Category 2', category_3: 'Category 3',
  category_4: 'Category 4', category_5: 'Category 5',
};
function IncidentCard({ incident }) {
  const cat = INCIDENT_CAT_LABEL[incident.category] || incident.category || 'Incident';
  let dateStr = '';
  try {
    const d = incident.incident_date || incident.created_at;
    if (d) dateStr = format(parseISO(String(d).slice(0, 10)), 'd MMM yyyy');
  } catch {}
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className="w-9 h-9 rounded-full bg-red-500 flex items-center justify-center text-white shrink-0 shadow">
          <AlertTriangle size={16} />
        </div>
        <div className="flex-1 w-px bg-slate-200 mt-2" />
      </div>
      <div className="flex-1 min-w-0 pb-6">
        <div className="mb-1">
          <span className="font-bold text-slate-800 text-sm">{incident.staff_name || incident.created_by_name || 'Staff'}</span>
          <span className="text-slate-500 text-sm"> reported an Incident</span>
        </div>
        <div className="bg-white border border-red-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 bg-red-50 border-b border-red-100 flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">{cat}</span>
            {incident.status && (
              <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-600 capitalize">
                {String(incident.status).replace(/_/g, ' ')}
              </span>
            )}
            {incident.ticket_name && <span className="text-xs text-slate-500">{incident.ticket_name}</span>}
          </div>
          <div className="px-5 py-4">
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{incident.description || '—'}</p>
            {incident.location && <p className="text-xs text-slate-400 mt-2">📍 {incident.location}</p>}
          </div>
          <div className="px-5 py-2.5 border-t border-slate-50 flex items-center justify-between">
            <span className="text-xs text-slate-400">{dateStr}</span>
            <a href="/incidents" className="text-xs font-semibold text-primary-600 hover:text-primary-700">View in Incidents →</a>
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterCategoriesDropdown({ open, onClose, selected, showPrivate, showPublic, onlyMine, onChange, categories = [] }) {
  const [local, setLocal]               = useState(selected);
  const [localPrivate, setLocalPrivate] = useState(showPrivate);
  const [localPublic, setLocalPublic]   = useState(showPublic);
  const [localMine, setLocalMine]       = useState(onlyMine);
  const ref = useRef();

  useEffect(() => { setLocal(selected); }, [selected]);
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const toggle = cat => setLocal(p => p.includes(cat) ? p.filter(c => c !== cat) : [...p, cat]);
  const selectAll = () => setLocal(categories);

  return (
    <div ref={ref} className="absolute right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-xl shadow-xl w-[min(520px,calc(100vw-2rem))]">
      <div className="p-4">
        {categories.length === 0 ? (
          <p className="text-sm text-slate-400 italic mb-4">No note categories yet.</p>
        ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-2 mb-4">
          {categories.map((cat, i) => (
            <label key={i} className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
              <input type="checkbox" checked={local.includes(cat)} onChange={() => toggle(cat)}
                className="w-4 h-4 text-primary-500 rounded" />
              {cat}
            </label>
          ))}
        </div>
        )}
        <div className="border-t border-slate-100 pt-3 flex items-center gap-6 flex-wrap">
          <button onClick={selectAll}
            className="ml-auto text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg px-3 py-1.5">
            Select all
          </button>
        </div>
      </div>
      <div className="px-4 pb-4 flex justify-end">
        <button onClick={() => { onChange(local, localPrivate, localPublic, localMine); onClose(); }}
          className="btn-primary text-sm px-6">Apply</button>
      </div>
    </div>
  );
}

// ─── Dual-month date range picker ─────────────────────────────────────────────
function DateRangePicker({ startDate, endDate, onChange, onClose }) {
  const [leftMonth, setLeftMonth] = useState(new Date());
  const [hovered, setHovered] = useState(null);
  const ref = useRef();

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const rightMonth = addMonths(leftMonth, 1);

  const handleDayClick = (d) => {
    if (!startDate || (startDate && endDate)) {
      onChange({ startDate: d, endDate: null });
    } else {
      if (d < startDate) onChange({ startDate: d, endDate: startDate });
      else onChange({ startDate, endDate: d });
      onClose();
    }
  };

  const quick = (label) => {
    const today = new Date();
    if (label === 'Last week') {
      onChange({ startDate: startOfWeek(today, {weekStartsOn: 1}), endDate: today });
    } else if (label === 'Last month') {
      const lm = subMonths(today, 1);
      onChange({ startDate: sfm(lm), endDate: endOfMonth(lm) });
    } else if (label === 'Last 3 months') {
      onChange({ startDate: sfm(subMonths(today, 3)), endDate: today });
    }
    onClose();
  };

  const MonthGrid = ({ month }) => {
    const firstDay = getDay(startOfMonth(month));
    const days = getDaysInMonth(month);
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));

    return (
      <div className="w-[220px]">
        <p className="text-center text-sm font-semibold text-slate-700 mb-3">
          {format(month, 'yyyy MMMM')}
        </p>
        <div className="grid grid-cols-7 text-center mb-1">
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
            <span key={d} className="text-[11px] font-medium text-slate-400">{d}</span>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-y-0.5 text-center text-sm">
          {cells.map((d, i) => {
            if (!d) return <div key={i} />;
            const isStart = startDate && isSameDay(d, startDate);
            const isEnd = endDate && isSameDay(d, endDate);
            const inRange = startDate && endDate && isWithinInterval(d, { start: startDate, end: endDate });
            const isHov = startDate && !endDate && hovered && d >= startDate && d <= hovered;
            return (
              <button key={i}
                onMouseEnter={() => setHovered(d)}
                onClick={() => handleDayClick(d)}
                className={clsx('h-8 w-full rounded text-sm transition-colors',
                  (isStart || isEnd) ? 'bg-primary-600 text-white font-bold' :
                  (inRange || isHov) ? 'bg-primary-100 text-primary-700' :
                  isToday(d) ? 'text-primary-600 font-bold' : 'text-slate-600 hover:bg-slate-100'
                )}>
                {d.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div ref={ref} className="absolute left-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-xl shadow-xl flex">
      <div className="border-r border-slate-100 p-4 flex flex-col gap-1 min-w-[130px]">
        {['Last week','Last month','Last 3 months'].map(l => (
          <button key={l} onClick={() => quick(l)}
            className="text-left text-sm text-slate-600 hover:text-primary-600 hover:bg-primary-50 px-3 py-2 rounded-lg transition-colors">
            {l}
          </button>
        ))}
      </div>
      <div className="p-4 flex gap-6">
        <div className="flex items-center gap-2 flex-col">
          <div className="flex items-center gap-2 mb-1">
            <button onClick={() => setLeftMonth(m => subMonths(m, 1))} className="p-1 hover:bg-slate-100 rounded text-slate-500"><ChevronLeft size={14}/></button>
            <span className="text-xs font-medium text-slate-500 w-4" />
          </div>
          <MonthGrid month={leftMonth} />
        </div>
        <div className="flex items-center gap-2 flex-col">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium text-slate-500 w-4" />
            <button onClick={() => setLeftMonth(m => addMonths(m, 1))} className="p-1 hover:bg-slate-100 rounded text-slate-500"><ChevronRight size={14}/></button>
          </div>
          <MonthGrid month={rightMonth} />
        </div>
      </div>
    </div>
  );
}

// ─── Add Note Modal ───────────────────────────────────────────────────────────
const FALLBACK_NOTE_TYPES = ['Note','Incident','Medication','Enquiry','Feedback','Injury','Invoice','Email','SMS'];
const MOOD_LABELS    = ['','Very Upset','Upset','Neutral','Happy','Very Happy'];
const MOOD_EMOJIS    = ['','😟','😕','😐','🙂','😊'];
const BEHAV_LABELS   = ['','Distressed','Unsettled','Neutral','Calm','Excellent'];
const ACTIVITIES_LIST = [
  'Personal Care','Meal Preparation','Medication Administration',
  'Community Access','Exercise & Recreation','Social Activities',
  'Domestic Assistance','Transportation','Learning Activities',
  'Communication Support','Therapy',
];

function AddNoteModal({ clientId, clientName, onClose, onSaved, editNote }) {
  const noteTypes = useNoteTypes();
  const displayTypes = noteTypes.filter(n => n.is_active).length > 0
    ? noteTypes.filter(n => n.is_active).map(n => n.name)
    : FALLBACK_NOTE_TYPES;
  const isEdit = !!editNote;
  const [form, setForm] = useState(editNote ? {
    type: editNote.note_type || displayTypes[0] || 'Note',
    content: editNote.note_content || '',
    isPrivate: !!editNote.is_private,
    moodRating: editNote.mood_rating || 0,
    behaviourRating: editNote.behaviour_rating || 0,
    activitiesCompleted: Array.isArray(editNote.activities_completed) ? editNote.activities_completed : [],
    goalIds: Array.isArray(editNote.goals_addressed) ? editNote.goals_addressed
           : (Array.isArray(editNote.linked_goals) ? editNote.linked_goals.map(g => g.id) : []),
    noteDate: String(editNote.note_date || editNote.shift_date || editNote.created_at || '').slice(0, 10),
  } : {
    type: displayTypes[0] || 'Note', content: '', isPrivate: false,
    moodRating: 0, behaviourRating: 0, activitiesCompleted: [], goalIds: [],
    // Defaults to today. Writing up an earlier visit means setting this back to that day, and the
    // note then files itself there instead of at the top of the list.
    noteDate: format(new Date(), 'yyyy-MM-dd'),
  });
  const [clientGoals, setClientGoals] = useState([]);
  const [saving, setSaving] = useState(false);
  const [rephrasing, setRephrasing] = useState(false);
  const [showRephraseMenu, setShowRephraseMenu] = useState(false);
  const [draftingNote, setDraftingNote] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get(`/clients/${clientId}/goals`).then(r => setClientGoals(r.data || [])).catch(() => {});
  }, [clientId]);

  const toggleActivity = (act) => setForm(f => ({
    ...f,
    activitiesCompleted: f.activitiesCompleted.includes(act)
      ? f.activitiesCompleted.filter(a => a !== act)
      : [...f.activitiesCompleted, act],
  }));

  const toggleGoal = (id) => setForm(f => ({
    ...f,
    goalIds: f.goalIds.includes(id) ? f.goalIds.filter(g => g !== id) : [...f.goalIds, id],
  }));

  const submit = async e => {
    e.preventDefault();
    if (!form.content.trim()) return toast.error('Please enter note content');
    setSaving(true);
    try {
      const payload = {
        content: form.content, type: form.type, isPrivate: form.isPrivate,
        moodRating: form.moodRating || null,
        behaviourRating: form.behaviourRating || null,
        activitiesCompleted: form.activitiesCompleted,
        goalIds: form.goalIds,
        noteDate: form.noteDate || undefined,
      };
      if (isEdit) {
        await api.put(`/clients/${clientId}/notes/${editNote.id}`, payload);
        toast.success('Note updated');
      } else {
        await api.post(`/clients/${clientId}/notes`, payload);
        toast.success('Note added');
      }
      onSaved();
      onClose();
    } catch { toast.error('Failed to save note'); }
    finally { setSaving(false); }
  };

  const generateDraft = async () => {
    setDraftingNote(true);
    const toastId = toast.loading('AI drafting note…');
    try {
      const { data } = await api.post('/ai/generate-progress-note', { clientId });
      if (data.note) set('content', data.note);
      toast.success('Draft generated', { id: toastId });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Draft failed', { id: toastId });
    } finally { setDraftingNote(false); }
  };

  const rephrase = async (mode = 'professional') => {
    if (!form.content.trim()) return toast.error('Write a note first');
    setRephrasing(true);
    setShowRephraseMenu(false);
    const toastId = toast.loading('AI rephrasing…');
    try {
      const { data } = await api.post('/ai/rephrase', { text: form.content, mode });
      set('content', data.text);
      toast.success(`Rephrased (${mode})`, { id: toastId });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Rephrase failed', { id: toastId });
    } finally { setRephrasing(false); }
  };

  const RatingRow = ({ label, field, labels }) => (
    <div>
      <label className="input-label mb-1">{label}</label>
      <div className="flex gap-1.5">
        {[1,2,3,4,5].map(n => (
          <button key={n} type="button"
            onClick={() => set(field, form[field] === n ? 0 : n)}
            className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-all ${
              form[field] === n
                ? 'bg-primary-500 text-white border-primary-500'
                : 'bg-slate-50 text-slate-500 border-slate-200 hover:border-primary-300'
            }`}>
            {field === 'moodRating' ? MOOD_EMOJIS[n] : n}
            <span className="block text-[9px] mt-0.5 leading-tight">{labels[n]}</span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl z-10 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-slate-100 shrink-0">
          <h2 className="text-lg font-bold text-slate-900">{isEdit ? 'Edit Progress Note' : 'Add Progress Note'}</h2>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400"><X size={16}/></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-5 overflow-y-auto flex-1">

          {/* Date + Type + Private */}
          <div className="flex items-center gap-3">
            <div className="w-44">
              <label className="input-label">Date of note</label>
              <input type="date" className="input-field" value={form.noteDate}
                max={format(new Date(), 'yyyy-MM-dd')}
                onChange={e => set('noteDate', e.target.value)} />
            </div>
            <div className="flex-1">
              <label className="input-label">Note Type</label>
              <select className="input-field" value={form.type} onChange={e => set('type', e.target.value)}>
                {displayTypes.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 mt-5 cursor-pointer">
              <button type="button" onClick={() => set('isPrivate', !form.isPrivate)}
                className={`w-10 h-5 rounded-full transition-colors relative ${form.isPrivate ? 'bg-primary-500' : 'bg-slate-200'}`}>
                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.isPrivate ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
              <Lock size={13} className="text-slate-400" />
              <span className="text-sm text-slate-600">Private</span>
            </label>
          </div>

          {/* Structured ratings */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100">
            <RatingRow label="Mood" field="moodRating" labels={MOOD_LABELS} />
            <RatingRow label="Behaviour" field="behaviourRating" labels={BEHAV_LABELS} />
          </div>

          {/* Activities checklist */}
          <div>
            <label className="input-label mb-2">Activities Completed</label>
            <div className="flex flex-wrap gap-2">
              {ACTIVITIES_LIST.map(act => (
                <button key={act} type="button" onClick={() => toggleActivity(act)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                    form.activitiesCompleted.includes(act)
                      ? 'bg-emerald-500 text-white border-emerald-500'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'
                  }`}>
                  {act}
                </button>
              ))}
            </div>
          </div>

          {/* NDIS Goals addressed */}
          {clientGoals.length > 0 && (
            <div>
              <label className="input-label mb-2">NDIS Goals Addressed</label>
              <div className="space-y-1.5">
                {clientGoals.filter(g => !g.closed_at && !g.achieved_at).map(g => (
                  <label key={g.id} className="flex items-center gap-2.5 cursor-pointer p-2 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-100">
                    <input type="checkbox" checked={form.goalIds.includes(g.id)} onChange={() => toggleGoal(g.id)}
                      className="w-4 h-4 text-primary-500 rounded" />
                    <span className="text-sm font-medium text-slate-800 flex-1">{g.name || g.goal_title || 'Untitled goal'}</span>
                    {g.rating > 0 && (
                      <span className="text-xs text-slate-400">{g.rating}/5</span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Note Content */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="input-label mb-0">Note Content</label>
              <div className="flex items-center gap-2">
                <button type="button" onClick={generateDraft} disabled={draftingNote}
                  className="text-xs flex items-center gap-1 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 px-2 py-1 rounded-md font-medium transition-colors disabled:opacity-50">
                  {draftingNote ? <Loader2 size={11} className="animate-spin"/> : <Sparkles size={11}/>}
                  AI Draft
                </button>
                <div className="relative">
                  <button type="button" onClick={() => setShowRephraseMenu(s => !s)}
                    disabled={rephrasing || !form.content.trim()}
                    className="text-xs flex items-center gap-1 bg-violet-100 text-violet-700 hover:bg-violet-200 px-2 py-1 rounded-md font-medium transition-colors disabled:opacity-50">
                    {rephrasing ? <Loader2 size={11} className="animate-spin"/> : <Sparkles size={11}/>}
                    AI Rephrase <ChevronDown size={10}/>
                  </button>
                  {showRephraseMenu && (
                    <div className="absolute right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[180px] z-20">
                      {[['professional','✨ Make professional'],['expand','📝 Expand with detail'],['concise','✂️ Make concise'],['grammar','✓ Fix grammar only']].map(([m,l]) => (
                        <button key={m} type="button" onClick={() => rephrase(m)}
                          className="w-full text-left text-xs px-3 py-1.5 hover:bg-slate-50">{l}</button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="relative">
              <textarea value={form.content} onChange={e => set('content', e.target.value)}
                className="input-field w-full resize-none" rows={5}
                placeholder={`Write a ${form.type.toLowerCase()}…`} required />
              <div className="absolute bottom-2 right-2">
                <VoiceButton onTranscript={t => set('content', v => v + (v ? ' ' : '') + t)} />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Saving…' : (isEdit ? 'Update Note' : 'Submit Note')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Outcome Assessment Modal ─────────────────────────────────────────────────
const OUTCOME_DOMAINS = [
  { key: 'daily_activities',    label: 'Daily Activities',              hint: 'Self-care, daily tasks, household' },
  { key: 'relationships',       label: 'Relationships',                 hint: 'Family, friends, community connections' },
  { key: 'home',                label: 'Home',                          hint: 'Living situation, safety, comfort' },
  { key: 'health_wellbeing',    label: 'Health & Wellbeing',            hint: 'Physical and mental health' },
  { key: 'lifelong_learning',   label: 'Lifelong Learning',             hint: 'Education, skills development' },
  { key: 'work',                label: 'Work',                          hint: 'Employment, vocational activities' },
  { key: 'social_participation','label': 'Social & Community',          hint: 'Community participation, civic life' },
  { key: 'choice_control',      label: 'Choice & Control',              hint: 'Decision-making, self-determination' },
];

function OutcomeModal({ clientId, onClose, onSaved }) {
  const [ratings, setRatings] = useState({});
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState(isoDate());
  const [saving, setSaving] = useState(false);

  const setR = (key, val) => setRatings(r => ({ ...r, [key]: r[key] === val ? 0 : val }));

  const submit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/outcomes`, { assessment_date: date, ...ratings, notes });
      toast.success('Outcome assessment saved');
      onSaved();
      onClose();
    } catch { toast.error('Failed to save outcome'); }
    finally { setSaving(false); }
  };

  const RATING_LABELS = ['','Poor','Below Average','Average','Good','Excellent'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl z-10 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-slate-100 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-slate-900">NDIS Outcome Assessment</h2>
            <p className="text-xs text-slate-400 mt-0.5">Rate each domain 1–5 based on the participant's current situation</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400"><X size={16}/></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className="input-label">Assessment Date</label>
            <input type="date" className="input-field !w-48" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="space-y-3">
            {OUTCOME_DOMAINS.map(({ key, label, hint }) => (
              <div key={key} className="flex items-center gap-4 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <div className="w-40 shrink-0">
                  <p className="text-sm font-semibold text-slate-700">{label}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>
                </div>
                <div className="flex gap-1.5 flex-1">
                  {[1,2,3,4,5].map(n => (
                    <button key={n} type="button" onClick={() => setR(key, n)}
                      className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-all ${
                        ratings[key] === n
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-white text-slate-400 border-slate-200 hover:border-primary-300 hover:text-primary-600'
                      }`}>
                      {n}
                      <span className="block text-[9px] font-normal mt-0.5">{RATING_LABELS[n]}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div>
            <label className="input-label">Additional Notes</label>
            <textarea className="input-field w-full resize-none" rows={3}
              value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Any context or observations about this assessment…" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Saving…' : 'Save Assessment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Note Timeline Card ───────────────────────────────────────────────────────
const MOOD_EMOJIS_CARD = ['','😟','😕','😐','🙂','😊'];
const MOOD_LABELS_CARD = ['','Very Upset','Upset','Neutral','Happy','Very Happy'];
const BEHAV_LABELS_CARD = ['','Distressed','Unsettled','Neutral','Calm','Excellent'];

function NoteCard({ note, clientId, onEdit }) {
  const { user } = useAuth();
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments]         = useState([]);
  const [loadingCmts, setLoadingCmts]   = useState(false);
  const [commentText, setCommentText]   = useState('');
  const [submitting, setSubmitting]     = useState(false);
  const isSystemNote = /^\[(MILEAGE|EXPENSE|FEEDBACK):/.test(note.note_content || '');
  const canEdit = !!user && !isSystemNote && (['admin','office_user','super_admin'].includes(user.role) || note.staff_id === user.id);

  const loadComments = async () => {
    setLoadingCmts(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/notes/${note.id}/comments`);
      setComments(data || []);
    } catch { /* silent */ }
    finally { setLoadingCmts(false); }
  };

  const toggleComments = () => {
    if (!showComments && comments.length === 0) loadComments();
    setShowComments(v => !v);
  };

  const submitComment = async e => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/notes/${note.id}/comments`, { content: commentText });
      setComments(c => [...c, data]);
      setCommentText('');
    } catch { toast.error('Failed to add comment'); }
    finally { setSubmitting(false); }
  };

  const staffInitials = (note.staff_name || 'U')
    .split(' ').map(w => w[0]).join('').toUpperCase().slice(0,2);
  const staffUpper = (note.staff_name || 'Unknown Staff').toUpperCase();

  const shiftLine = note.shift_date
    ? `${note.staff_name} added a ${codeWords(note.note_type) || 'Note'} for ${note.client_name || ''} @ ${
        format(parseISO(note.shift_date), 'dd/MM/yyyy')
      } ${note.start_time ? fmtTime12(note.start_time) : ''} - ${note.end_time ? fmtTime12(note.end_time) : ''}`
    : null;

  const activities = Array.isArray(note.activities_completed) ? note.activities_completed : [];
  const linkedGoals = Array.isArray(note.linked_goals) ? note.linked_goals : [];

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs font-bold shrink-0 shadow">
          {staffInitials}
        </div>
        <div className="flex-1 w-px bg-slate-200 mt-2" />
      </div>
      <div className="flex-1 min-w-0 pb-6">
        <div className="mb-1">
          <span className="font-bold text-slate-800 text-sm">{staffUpper}</span>
          <span className="text-slate-500 text-sm"> added a {codeWords(note.note_type) || 'Note'}</span>
          {note.is_private && (
            <span className="ml-2 inline-flex items-center gap-1 text-[11px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
              <Lock size={10}/> Private
            </span>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {shiftLine && (
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
              <p className="text-sm font-semibold text-primary-600">{shiftLine}</p>
            </div>
          )}
          {/* Structured fields */}
          {(note.mood_rating || note.behaviour_rating || activities.length > 0 || linkedGoals.length > 0) && (
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 space-y-2">
              <div className="flex flex-wrap gap-3">
                {note.mood_rating > 0 && (
                  <span className="inline-flex items-center gap-1.5 text-xs bg-white border border-slate-200 rounded-full px-3 py-1 font-medium text-slate-700">
                    {MOOD_EMOJIS_CARD[note.mood_rating]} Mood: {MOOD_LABELS_CARD[note.mood_rating]}
                  </span>
                )}
                {note.behaviour_rating > 0 && (
                  <span className="inline-flex items-center gap-1.5 text-xs bg-white border border-slate-200 rounded-full px-3 py-1 font-medium text-slate-700">
                    Behaviour: {BEHAV_LABELS_CARD[note.behaviour_rating]} ({note.behaviour_rating}/5)
                  </span>
                )}
              </div>
              {activities.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {activities.map(a => (
                    <span key={a} className="text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-full px-2.5 py-0.5 font-medium">{a}</span>
                  ))}
                </div>
              )}
              {linkedGoals.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {linkedGoals.map(g => (
                    <span key={g.id} className="text-[11px] bg-violet-50 text-violet-700 border border-violet-100 rounded-full px-2.5 py-0.5 font-medium">
                      🎯 {g.title}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="px-5 py-4">
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{note.note_content}</p>
          </div>
          <div className="px-5 py-2.5 border-t border-slate-50 flex items-center justify-between">
            <span className="text-xs text-slate-400">
              {(() => {
                const covers = String(note.note_date || note.shift_date || note.created_at || '').slice(0, 10);
                const typed  = String(note.created_at || '').slice(0, 10);
                // Show the day the note covers. When it was written up later, show that too —
                // the record has to be honest about when it was actually entered.
                return (
                  <>
                    {covers ? format(parseISO(covers), 'dd/MM/yyyy') : ''}
                    {covers && typed && covers !== typed && (
                      <span className="ml-1 italic">· recorded {format(parseISO(note.created_at), 'dd/MM/yyyy hh:mm a')}</span>
                    )}
                    {covers === typed && note.created_at && (
                      <span className="ml-1">{format(parseISO(note.created_at), 'hh:mm a')}</span>
                    )}
                  </>
                );
              })()}
              {note.updated_at && note.updated_at !== note.created_at && <span className="ml-1 italic">· edited</span>}
            </span>
            <div className="flex items-center gap-3">
              {canEdit && (
                <button onClick={() => onEdit?.(note)} className="text-xs text-slate-500 hover:text-primary-600 flex items-center gap-1">
                  <Pencil size={12}/> Edit
                </button>
              )}
              <button onClick={toggleComments} className="text-xs text-primary-600 hover:underline">
                {showComments ? 'Hide comments' : `Add comment${comments.length > 0 ? ` (${comments.length})` : ''}`}
              </button>
            </div>
          </div>

          {/* Inline comments section */}
          {showComments && (
            <div className="border-t border-slate-100 px-5 py-3 space-y-3 bg-slate-50/60">
              {loadingCmts && <p className="text-xs text-slate-400">Loading…</p>}
              {comments.map(c => (
                <div key={c.id} className="flex gap-2.5">
                  <div className="w-6 h-6 rounded-full bg-slate-300 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                    {(c.staff_name || 'U').split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <span className="text-xs font-semibold text-slate-700">{c.staff_name || 'Staff'}</span>
                    <span className="text-[10px] text-slate-400 ml-2">{format(parseISO(c.created_at), 'dd/MM hh:mm a')}</span>
                    <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{c.content}</p>
                  </div>
                </div>
              ))}
              <form onSubmit={submitComment} className="flex gap-2 pt-1">
                <input
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  placeholder="Write a comment…"
                  className="flex-1 text-xs border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-primary-400"
                />
                <button type="submit" disabled={submitting || !commentText.trim()}
                  className="text-xs px-3 py-1.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-40 transition-colors">
                  {submitting ? '…' : 'Post'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ClientCommunications() {
  const { id } = useParams();
  const record = useClientRecord(id);
  const { user } = useAuth();
  const [notes, setNotes]       = useState([]);
  const [incidents, setIncidents] = useState([]);   // shown inline in the timeline
  const [loading, setLoading]   = useState(true);
  const [showAdd, setShowAdd]       = useState(false);
  const [editNote, setEditNote]     = useState(null);
  const [showOutcome, setShowOutcome] = useState(false);
  const [showDate, setShowDate]     = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [showDownload, setShowDownload] = useState(false);
  const [downloading, setDownloading]   = useState(false);
  const downloadRef = useRef();
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [activeCategories, setActiveCategories] = useState([]);
  const [filterShowPrivate, setFilterShowPrivate] = useState(true);
  const [filterShowPublic, setFilterShowPublic]   = useState(true);
  const [filterOnlyMine, setFilterOnlyMine]       = useState(false);

  // Filter categories = the note types that ACTUALLY exist on this client's notes
  // (canonicalised), PLUS 'Incident' when the client has incidents — so every checkbox is
  // meaningful and incidents can be toggled too. Defaults to all-selected.
  const catOptions = useMemo(() => {
    const cats = [...new Set(notes.map(n => canonCat(n.note_type)))];
    if (incidents.length) cats.push('Incident');
    return [...new Set(cats)].sort();
  }, [notes, incidents]);
  useEffect(() => { setActiveCategories(catOptions); }, [catOptions]);
  // Incidents are shown/exported only when the 'Incident' category is active (or no filter set).
  const showIncidents = !activeCategories.length || activeCategories.includes('Incident');

  const loadNotes = useCallback(() => {
    const params = new URLSearchParams();
    if (dateRange.startDate) params.set('startDate', format(dateRange.startDate, 'yyyy-MM-dd'));
    if (dateRange.endDate)   params.set('endDate',   format(dateRange.endDate,   'yyyy-MM-dd'));
    api.get(`/clients/${id}/notes?${params}`)
      .then(r => setNotes(r.data))
      .catch(() => toast.error('Failed to load notes'))
      .finally(() => setLoading(false));
    api.get(`/clients/${id}/incidents`)
      .then(r => setIncidents(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, [id, dateRange]);

  // Clients have no email or phone of their own: use the participant's mobile, then the primary
  // contact on file.
  const primary = primaryContactOf(record.client);
  const clientContact = {
    email: primary?.email || '',
    phone: record.client?.mobile || primary?.phone || '',
  };

  useEffect(() => {
    setLoading(true);
    loadNotes();
  }, [id, loadNotes]);

  // Apply category + privacy + mine filter
  const filteredNotes = notes.filter(n => {
    if (!filterShowPrivate && n.is_private) return false;
    if (!filterShowPublic && !n.is_private) return false;
    if (filterOnlyMine && n.staff_id !== user?.id) return false;
    // Category filter against the real, existing note categories.
    if (activeCategories.length && !activeCategories.includes(canonCat(n.note_type))) return false;
    return true;
  });

  // Merge notes + incidents into one date-grouped timeline (incidents respect the date range).
  const inRange = (d) => {
    if (!d) return true;
    const day = String(d).slice(0, 10);
    if (dateRange.startDate && day < format(dateRange.startDate, 'yyyy-MM-dd')) return false;
    if (dateRange.endDate && day > format(dateRange.endDate, 'yyyy-MM-dd')) return false;
    return true;
  };
  const rangedIncidents = showIncidents ? incidents.filter(ic => inRange(ic.incident_date || ic.created_at)) : [];
  const timelineItems = [
    // note_date is the day the note is ABOUT. Grouping on created_at put a note for the 4th
    // under the day it was typed up, which is how a late write-up ended up below the 19th.
    ...filteredNotes.map(n => ({ kind: 'note', key: `n${n.id}`, day: String(n.note_date || n.shift_date || n.created_at || '').slice(0, 10), n })),
    ...rangedIncidents.map(ic => ({ kind: 'incident', key: `i${ic.id}`, day: String(ic.incident_date || ic.created_at || '').slice(0, 10), ic })),
  ];
  // Days run newest first, but within a day everything reads in the order it happened: the
  // morning shift's note, then the afternoon's, then the sleepover. Incidents interleave by
  // their own time rather than sinking below every note. A note takes its time from its shift;
  // '~' sorts after any digit, so anything untimed falls to the end of its day.
  const timeOf = (item) => {
    const t = item.kind === 'incident'
      ? (item.ic.incident_time || String(item.ic.created_at || '').slice(11, 19))
      : (item.n.start_time || String(item.n.created_at || '').slice(11, 19));
    return t ? String(t).slice(0, 8).padStart(8, '0') : '~';
  };
  const grouped = timelineItems.reduce((acc, item) => {
    const day = item.day || 'Unknown';
    (acc[day] = acc[day] || []).push(item);
    return acc;
  }, {});
  Object.values(grouped).forEach(list => list.sort((a, b) => timeOf(a).localeCompare(timeOf(b))));

  // Close download dropdown on outside click
  useEffect(() => {
    if (!showDownload) return;
    const handler = (e) => { if (downloadRef.current && !downloadRef.current.contains(e.target)) setShowDownload(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDownload]);

  const clientName = fullName(record.client);

  // Merge progress notes + incidents (respecting the active filters + date range) into one
  // chronological export, so "Download" gives a single clean document with BOTH.
  const buildExportItems = () => {
    const noteItems = filteredNotes.map(n => ({ kind: 'note', data: n }));
    const incItems  = rangedIncidents.map(ic => ({ kind: 'incident', data: ic }));
    return [...noteItems, ...incItems];
  };

  const handleDownloadPDF = async () => {
    const items = buildExportItems();
    if (!items.length) return toast.error('Nothing to download');
    setShowDownload(false);
    const branding = await getBranding();
    exportProgressPDF(items, { clientName, branding });
    toast.success(`PDF ready — ${items.length} entr${items.length !== 1 ? 'ies' : 'y'} in chronological order`);
  };

  const handleDownloadDocx = async () => {
    const items = buildExportItems();
    if (!items.length) return toast.error('Nothing to download');
    setShowDownload(false);
    setDownloading(true);
    try {
      const branding = await getBranding();
      await exportProgressDocx(items, { clientName, branding });
      toast.success(`Word doc ready — ${items.length} entr${items.length !== 1 ? 'ies' : 'y'} in chronological order`);
    } catch { toast.error('Failed to generate Word document'); }
    finally { setDownloading(false); }
  };

  function emailClient() {
    if (!clientContact.email) return toast.error('No email on file. Add one to the primary contact on the Overview.');
    window.location.href = `mailto:${clientContact.email}`;
  }

  function smsClient() {
    if (!clientContact.phone) return toast.error('No mobile or contact phone on file. Add one on the Overview.');
    window.location.href = `sms:${clientContact.phone}`;
  }

  const dateLabel = () => {
    const { startDate, endDate } = dateRange;
    if (startDate && endDate) return `${format(startDate,'dd/MM/yyyy')} – ${format(endDate,'dd/MM/yyyy')}`;
    if (startDate) return `From ${format(startDate,'dd/MM/yyyy')}`;
    return null;
  };

  return (
    <ClientFrame record={record}>

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <button onClick={() => setShowAdd(true)} className="btn-primary text-sm flex items-center gap-1.5">
          <Plus size={14} /> Add Notes
        </button>
        <button onClick={() => setShowOutcome(true)} className="btn-secondary text-sm flex items-center gap-1.5">
          <Sparkles size={14} /> Record Outcome
        </button>

        <div className="flex-1" />

        {/* Download dropdown — PDF / Word */}
        <div className="relative" ref={downloadRef}>
          <button
            onClick={() => setShowDownload(d => !d)}
            disabled={downloading}
            className={clsx(
              'flex items-center gap-1.5 border rounded-xl px-3 py-2 text-sm transition-colors',
              showDownload ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'
            )}>
            {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            Download <ChevronDown size={11} />
          </button>
          {showDownload && (
            <div className="absolute right-0 top-full mt-1.5 z-50 bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 min-w-[180px]">
              <div className="px-3 py-1.5 border-b border-slate-100 mb-1">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Chronological order (1 → N)</p>
              </div>
              <button
                onClick={handleDownloadPDF}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-red-50 hover:text-red-700 text-left transition-colors">
                <FileText size={14} className="text-red-500" />
                Download as PDF
              </button>
              <button
                onClick={handleDownloadDocx}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 text-left transition-colors">
                <FileType size={14} className="text-blue-500" />
                Download as Word (.docx)
              </button>
            </div>
          )}
        </div>

        {/* Date range picker */}
        <div className="relative">
          <button
            onClick={() => { setShowDate(d => !d); setShowFilter(false); }}
            className={clsx('flex items-center gap-2 border rounded-xl px-3 py-2 text-sm transition-colors',
              showDate ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'
            )}>
            <CalendarDays size={14} />
            <span>{dateLabel() || 'Start date'}</span>
            {dateLabel() && <span className="text-slate-400">–</span>}
            {!dateLabel() && <span className="text-slate-400 mx-1">-</span>}
            <span className="text-slate-400">{dateLabel() ? '' : 'End date'}</span>
            {dateLabel() && (
              <button onClick={e => { e.stopPropagation(); setDateRange({ startDate: null, endDate: null }); }}
                className="p-0.5 hover:text-red-500 text-slate-400">
                <X size={12} />
              </button>
            )}
          </button>
          {showDate && (
            <DateRangePicker
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={r => setDateRange(r)}
              onClose={() => setShowDate(false)}
            />
          )}
        </div>

        <button onClick={smsClient} className="btn-secondary text-sm flex items-center gap-1.5" title={clientContact.phone || 'No phone on file'}>
          <MessageSquare size={13} /> SMS
        </button>
        <button onClick={emailClient} className="btn-secondary text-sm flex items-center gap-1.5" title={clientContact.email || 'No email on file'}>
          <Mail size={13} /> Email
        </button>

        {/* Filter Categories */}
        <div className="relative">
          <button
            onClick={() => { setShowFilter(f => !f); setShowDate(false); }}
            className={clsx('flex items-center gap-1.5 border rounded-xl px-3 py-2 text-sm transition-colors',
              showFilter ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'
            )}>
            <Filter size={13} /> Filter Categories <ChevronDown size={12} />
          </button>
          <FilterCategoriesDropdown
            open={showFilter}
            onClose={() => setShowFilter(false)}
            categories={catOptions}
            selected={activeCategories}
            showPrivate={filterShowPrivate}
            showPublic={filterShowPublic}
            onlyMine={filterOnlyMine}
            onChange={(cats, priv, pub, mine) => {
              setActiveCategories(cats);
              setFilterShowPrivate(priv);
              setFilterShowPublic(pub);
              setFilterOnlyMine(mine);
            }}
          />
        </div>
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="card p-12 text-center text-slate-400 italic">No notes recorded yet</div>
      ) : (
        <div>
          {Object.entries(grouped).sort(([a],[b]) => b.localeCompare(a)).map(([day, items]) => (
            <div key={day}>
              {/* Date badge */}
              <div className="flex items-center gap-3 mb-4">
                <span className="bg-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg">
                  {(() => { try { return format(parseISO(day), 'd MMM, yyyy'); } catch { return day; } })()}
                </span>
                <div className="flex-1 h-px bg-slate-100" />
              </div>
              <div className="ml-2">
                {items.map(item => item.kind === 'incident'
                  ? <IncidentCard key={item.key} incident={item.ic} />
                  : <NoteCard key={item.key} note={item.n} clientId={id} onEdit={setEditNote} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <AddNoteModal
          clientId={id}
          clientName={clientName}
          onClose={() => setShowAdd(false)}
          onSaved={() => { loadNotes(); }}
        />
      )}
      {editNote && (
        <AddNoteModal
          clientId={id}
          clientName={clientName}
          editNote={editNote}
          onClose={() => setEditNote(null)}
          onSaved={() => { loadNotes(); }}
        />
      )}
      {showOutcome && (
        <OutcomeModal
          clientId={id}
          onClose={() => setShowOutcome(false)}
          onSaved={() => {}}
        />
      )}
    </ClientFrame>
  );
}
